import type { Note, PitchClass, Accidental, Clef, DifficultyLevel } from '../types/music';
import { getMidiNumber, calculateStaffPosition } from '../utils/noteMappings';

/**
 * Generate a unique ID for a note
 */
function generateNoteId(clef: Clef, pitchClass: PitchClass, octave: number, accidental: Accidental): string {
  const accidentalStr = accidental === 'natural' ? '' : `-${accidental}`;
  return `${clef}-${pitchClass}${octave}${accidentalStr}`;
}

/**
 * Create a Note object with all computed properties
 */
function createNote(
  pitchClass: PitchClass,
  octave: number,
  accidental: Accidental,
  clef: Clef,
  difficultyLevel: DifficultyLevel
): Note {
  return {
    id: generateNoteId(clef, pitchClass, octave, accidental),
    pitchClass,
    accidental,
    midiNumber: getMidiNumber(pitchClass, accidental, octave),
    octave,
    clef,
    staffPosition: calculateStaffPosition(pitchClass, octave, clef),
    difficultyLevel
  };
}

/**
 * Treble clef notes
 *
 * Easy (C4 - G5): Notes on the staff lines and spaces, plus middle C
 * Medium (B3 - A5): Extends one note below and above easy range
 * Hard (G3 - C6): Includes ledger lines
 */
export const TREBLE_CLEF_NOTES: Note[] = [
  // Octave 3 (ledger lines below - hard)
  createNote('G', 3, 'natural', 'treble', 'hard'),
  createNote('A', 3, 'natural', 'treble', 'hard'),
  createNote('B', 3, 'natural', 'treble', 'medium'),

  // Octave 4 (middle C and up - easy core range)
  createNote('C', 4, 'natural', 'treble', 'easy'),   // Middle C - ledger line below
  createNote('D', 4, 'natural', 'treble', 'easy'),   // Below staff
  createNote('E', 4, 'natural', 'treble', 'easy'),   // Line 1
  createNote('F', 4, 'natural', 'treble', 'easy'),   // Space 1
  createNote('G', 4, 'natural', 'treble', 'easy'),   // Line 2
  createNote('A', 4, 'natural', 'treble', 'easy'),   // Space 2
  createNote('B', 4, 'natural', 'treble', 'easy'),   // Line 3 (middle)

  // Octave 5 (upper staff - easy core continues)
  createNote('C', 5, 'natural', 'treble', 'easy'),   // Space 3
  createNote('D', 5, 'natural', 'treble', 'easy'),   // Line 4
  createNote('E', 5, 'natural', 'treble', 'easy'),   // Space 4
  createNote('F', 5, 'natural', 'treble', 'easy'),   // Line 5
  createNote('G', 5, 'natural', 'treble', 'easy'),   // Above staff
  createNote('A', 5, 'natural', 'treble', 'medium'), // Ledger line above
  createNote('B', 5, 'natural', 'treble', 'hard'),

  // Octave 6 (ledger lines above - hard)
  createNote('C', 6, 'natural', 'treble', 'hard'),

  // Sharps for treble clef (medium/hard difficulty)
  createNote('C', 4, 'sharp', 'treble', 'medium'),
  createNote('D', 4, 'sharp', 'treble', 'medium'),
  createNote('F', 4, 'sharp', 'treble', 'medium'),
  createNote('G', 4, 'sharp', 'treble', 'medium'),
  createNote('A', 4, 'sharp', 'treble', 'medium'),
  createNote('C', 5, 'sharp', 'treble', 'medium'),
  createNote('D', 5, 'sharp', 'treble', 'medium'),
  createNote('F', 5, 'sharp', 'treble', 'hard'),
  createNote('G', 5, 'sharp', 'treble', 'hard'),

  // Flats for treble clef (medium/hard difficulty)
  createNote('D', 4, 'flat', 'treble', 'medium'),
  createNote('E', 4, 'flat', 'treble', 'medium'),
  createNote('G', 4, 'flat', 'treble', 'medium'),
  createNote('A', 4, 'flat', 'treble', 'medium'),
  createNote('B', 4, 'flat', 'treble', 'medium'),
  createNote('D', 5, 'flat', 'treble', 'medium'),
  createNote('E', 5, 'flat', 'treble', 'hard'),
  createNote('G', 5, 'flat', 'treble', 'hard'),
  createNote('A', 5, 'flat', 'treble', 'hard'),
  createNote('B', 5, 'flat', 'treble', 'hard'),
];

/**
 * Bass clef notes
 *
 * Easy (C3 - C4): "Bass C through Middle C" - notes on/near the staff
 * Medium (G2 - D4): Extends the range with some ledger lines
 * Hard (E2 - F4): Full range with more ledger lines
 *
 * Note: The "easy" range starts at C3 (second line from bottom of bass staff)
 * and goes to C4 (Middle C, one ledger line above bass staff).
 * This covers the most commonly-read bass clef notes.
 */
export const BASS_CLEF_NOTES: Note[] = [
  // Octave 2 (low ledger lines - hard/medium)
  createNote('E', 2, 'natural', 'bass', 'hard'),     // Low ledger line
  createNote('F', 2, 'natural', 'bass', 'hard'),     // Low ledger line (first unlock)
  createNote('G', 2, 'natural', 'bass', 'medium'),   // Line 1
  createNote('A', 2, 'natural', 'bass', 'medium'),   // Space 1
  createNote('B', 2, 'natural', 'bass', 'medium'),   // Line 2

  // Octave 3 (main bass staff - easy core range starts here)
  createNote('C', 3, 'natural', 'bass', 'easy'),     // Space 2 - Bass C (start of easy range)
  createNote('D', 3, 'natural', 'bass', 'easy'),     // Line 3 (middle line)
  createNote('E', 3, 'natural', 'bass', 'easy'),     // Space 3
  createNote('F', 3, 'natural', 'bass', 'easy'),     // Line 4
  createNote('G', 3, 'natural', 'bass', 'easy'),     // Space 4
  createNote('A', 3, 'natural', 'bass', 'easy'),     // Line 5
  createNote('B', 3, 'natural', 'bass', 'easy'),     // Just above staff

  // Octave 4 (ledger lines above - easy ends at C4)
  createNote('C', 4, 'natural', 'bass', 'easy'),     // Middle C - ledger line (end of easy range)
  createNote('D', 4, 'natural', 'bass', 'medium'),   // Above ledger line
  createNote('E', 4, 'natural', 'bass', 'hard'),     // High ledger line
  createNote('F', 4, 'natural', 'bass', 'hard'),     // High ledger line

  // Sharps for bass clef (medium/hard difficulty)
  createNote('C', 3, 'sharp', 'bass', 'medium'),
  createNote('D', 3, 'sharp', 'bass', 'medium'),
  createNote('F', 3, 'sharp', 'bass', 'medium'),
  createNote('G', 3, 'sharp', 'bass', 'medium'),
  createNote('A', 3, 'sharp', 'bass', 'medium'),
  createNote('C', 4, 'sharp', 'bass', 'hard'),
  createNote('F', 2, 'sharp', 'bass', 'hard'),
  createNote('G', 2, 'sharp', 'bass', 'hard'),

  // Flats for bass clef (medium/hard difficulty)
  createNote('D', 3, 'flat', 'bass', 'medium'),
  createNote('E', 3, 'flat', 'bass', 'medium'),
  createNote('G', 3, 'flat', 'bass', 'medium'),
  createNote('A', 3, 'flat', 'bass', 'medium'),
  createNote('B', 3, 'flat', 'bass', 'medium'),
  createNote('D', 4, 'flat', 'bass', 'hard'),
  createNote('E', 4, 'flat', 'bass', 'hard'),
  createNote('A', 2, 'flat', 'bass', 'hard'),
  createNote('B', 2, 'flat', 'bass', 'hard'),
];

/**
 * All available notes combined
 */
export const ALL_NOTES: Note[] = [...TREBLE_CLEF_NOTES, ...BASS_CLEF_NOTES];

/**
 * Get notes for a specific clef
 */
export function getNotesByClef(clef: Clef): Note[] {
  return clef === 'treble' ? TREBLE_CLEF_NOTES : BASS_CLEF_NOTES;
}

/**
 * Get notes filtered by difficulty
 */
export function getNotesByDifficulty(notes: Note[], maxDifficulty: DifficultyLevel): Note[] {
  const difficultyOrder: Record<DifficultyLevel, number> = {
    'easy': 1,
    'medium': 2,
    'hard': 3
  };

  const maxLevel = difficultyOrder[maxDifficulty];
  return notes.filter(note => difficultyOrder[note.difficultyLevel] <= maxLevel);
}

/**
 * Get notes filtered by accidental type
 */
export function getNotesByAccidental(
  notes: Note[],
  includeNaturals: boolean,
  includeSharps: boolean,
  includeFlats: boolean
): Note[] {
  return notes.filter(note => {
    if (note.accidental === 'natural' && includeNaturals) return true;
    if (note.accidental === 'sharp' && includeSharps) return true;
    if (note.accidental === 'flat' && includeFlats) return true;
    return false;
  });
}

/**
 * Get notes filtered by MIDI range
 */
export function getNotesByMidiRange(notes: Note[], lowMidi: number, highMidi: number): Note[] {
  return notes.filter(note => note.midiNumber >= lowMidi && note.midiNumber <= highMidi);
}

/**
 * Find a note by its ID
 */
export function findNoteById(id: string): Note | undefined {
  return ALL_NOTES.find(note => note.id === id);
}

/**
 * Get the MIDI range for a given note range string (e.g., "C3" to "C4")
 */
export function getMidiRangeFromStrings(low: string, high: string): { lowMidi: number; highMidi: number } {
  const lowMatch = low.match(/^([A-G])([#b]?)(\d)$/);
  const highMatch = high.match(/^([A-G])([#b]?)(\d)$/);

  if (!lowMatch || !highMatch) {
    // Default to a reasonable range if parsing fails
    return { lowMidi: 48, highMidi: 72 }; // C3 to C5
  }

  const parsePitch = (match: RegExpMatchArray) => {
    const pitchClass = match[1] as PitchClass;
    const accidental = match[2] === '#' ? 'sharp' : match[2] === 'b' ? 'flat' : 'natural';
    const octave = parseInt(match[3], 10);
    return getMidiNumber(pitchClass, accidental as Accidental, octave);
  };

  return {
    lowMidi: parsePitch(lowMatch),
    highMidi: parsePitch(highMatch)
  };
}
